import {
  FORMATIONS,
  tacticSlots,
  HUMAN_OFFER_WINDOW,
  KEEPER_ATTRS,
  autoLineup,
  OUTFIELD_ATTRS,
  POSITIONS,
  POSITION_WEIGHTS,
  achievementCatalogue,
  clubLeague,
  clubPlayers,
  effectiveStrength,
  expectedSalary,
  getClub,
  getManager,
  highlights,
  isWindowOpen,
  humanClubIds,
  playerValue,
  minimumAcceptable,
  newsPayload,
  reputationKey,
  rivalryKey,
  positionOf,
  canLoan,
  isReportStale,
  requiredLoanShare,
  requiredSalary,
  scoutCost,
  scoutReportFor,
  rivalryIntensity,
  squadStrength,
  youthViews,
  timeBView,
  livresView,
  YOUTH_MAX_AGE,
  squadWageBill,
  upcomingFixture,
  type AttrKey,
  type Club,
  type Lineup,
  type MatchResult,
  type Offer,
  type Player,
  type Position,
  type World,
} from "@legacy-manager/engine";
import { buildJornal } from "./jornal";

import {
  buildContinental,
  buildCalendar,
  buildCareer,
  buildCup,
  buildState,
  buildManagement,
  buildScorers,
  toTableRows,
} from "./competition-views";
import type {
  ClubOption,
  ClubView,
  MatchSummary,
  ClubPreview,
  OfferRow,
  OpponentView,
  PlayerDetail,
  PlayerRow,
  QueuedDecision,
  RivalRow,
  Sector,
  ScoutView,
  SeasonReportView,
  TableRow,
  YouthRow,
  ClubeDoTimeB,
  LivreRow,
  TimeBView,
} from "./types";

/**
 * Transforma o mundo do motor nas projeções que a interface desenha.
 *
 * Funções puras de propósito: recebem o mundo e devolvem dados, sem guardar
 * estado. É o mesmo trabalho que o servidor fará ao montar a visão filtrada de
 * cada membro de uma liga online.
 */

const SECTOR_OF: Record<string, Sector> = {
  GOL: "GOL",
  ZAG: "DEF",
  LD: "DEF",
  LE: "DEF",
  VOL: "MEI",
  MC: "MEI",
  MEI: "MEI",
  PD: "ATA",
  PE: "ATA",
  ATA: "ATA",
};

/**
 * Os quatorze atributos, na ordem em que a ficha os mostra.
 *
 * Vem das duas listas do motor, e não de uma tabela própria: a tabela que
 * existia aqui guardava a sigla e o nome escrito em português, e o nome não é
 * mais desta camada, é do dicionário. O que sobrou é a ordem.
 */
const ATTR_ORDER: readonly AttrKey[] = [...OUTFIELD_ATTRS, ...KEEPER_ATTRS];

export function sectorOf(pos: string): Sector {
  return SECTOR_OF[pos] ?? "MEI";
}

export function sectorOrder(sector: Sector): number {
  return sector === "GOL" ? 0 : sector === "DEF" ? 1 : sector === "MEI" ? 2 : 3;
}

export function buildOptions(world: World): ClubOption[] {
  const options: ClubOption[] = [];
  for (const club of Object.values(world.clubs)) {
    const league = clubLeague(world, club.id);
    options.push({
      id: club.id,
      name: club.name,
      short: club.short,
      colors: club.colors,
      countryId: club.countryId,
      countryName: world.countries[club.countryId]?.name ?? "",
      leagueName: league?.name ?? "",
      tier: league?.tier ?? 0,
      reputation: club.reputation,
      reputationKey: reputationKey(club.reputation),
      squadStrength: Math.round(squadStrength(world, club.id)),
      cash: club.cash,
    });
  }
  return options.sort(
    (a, b) =>
      a.countryName.localeCompare(b.countryName) ||
      a.tier - b.tier ||
      b.squadStrength - a.squadStrength,
  );
}

export function playerRow(
  player: Player,
  starters: Set<string>,
  bench: Set<string>,
  playingPos: string | null,
): PlayerRow {
  return {
    id: player.id,
    name: player.name,
    pos: player.pos,
    sector: sectorOf(player.pos),
    age: player.age,
    nationality: player.nationality,
    strength: player.strength,
    highlights: highlights(player),
    morale: Math.round(player.morale),
    condition: Math.round(player.condition),
    value: player.value,
    salary: player.contract.salary,
    shirt: player.shirt,
    injuryRounds: player.injuryRounds,
    suspensionRounds: player.suspensionRounds,
    goals: player.season.goals,
    assists: player.season.assists,
    apps: player.season.apps,
    yellow: player.season.yellow,
    rating:
      player.season.apps > 0
        ? Math.round((player.season.ratingSum / player.season.apps) * 10) / 10
        : 0,
    starter: starters.has(player.id),
    benched: bench.has(player.id),
    playingPos,
    contractEndsSeason: player.contract.endsSeason,
    transferListed: player.transferListed,
    star: player.star === true,
  };
}

export function playerDetail(world: World, player: Player, myClubId: string): PlayerDetail {
  const club = player.clubId ? world.clubs[player.clubId] : null;
  const weights = POSITION_WEIGHTS[player.pos];
  const keys = ATTR_ORDER;
  const asking = requiredSalary(world, player, myClubId);

  return {
    ...playerRow(player, new Set(), new Set(), null),
    // Vazio, e não "Sem clube": a frase do jogador livre é da tela, que sabe em
    // que idioma escrevê-la. Quem lê usa o `clubId` para saber qual é o caso.
    clubName: club?.name ?? "",
    clubId: club?.id ?? null,
    clubShort: club?.short ?? "",
    clubColors: club?.colors ?? null,
    clubCrest: club?.crest ?? null,
    // Pé, personalidade e característica saem como sigla do motor, e não como
    // palavra: os três já existem escritos nos três dicionários, e traduzi-los
    // aqui deixava a ficha meio em português para quem joga em outro idioma.
    foot: player.foot,
    personality: player.personality,
    attrs: keys.map((key) => ({
      key,
      value: player.attrs[key] ?? 0,
      weighted: weights[key] !== undefined,
    })),
    careerApps: player.career.apps,
    careerGoals: player.career.goals,
    yellow: player.season.yellow,
    red: player.season.red,
    askingSalary: Number.isFinite(asking) ? asking : null,
    askingPrice: player.clubId ? minimumAcceptable(player, world.season) : 0,
    buyPrice: precoDeCompra(world, player),
    isMine: player.clubId === myClubId,
    scout: scoutView(world, player, myClubId),
    scoutCost: scoutCost(world, player),
    loanShare: requiredLoanShare(player),
    loanBlocked: canLoan(world, player.id, myClubId).reason,
    auction: auctionView(world, player, myClubId),
    onLoan: player.loan
      ? {
          ownerName: world.clubs[player.loan.ownerId]?.name ?? "",
          borrowerName: club?.name ?? "",
          salaryShare: player.loan.salaryShare,
        }
      : null,
  };
}

/**
 * Laudo de olheiro pronto para a tela.
 *
 * O potencial em si nunca sai daqui: o que a interface recebe é a faixa, e a
 * marca de laudo velho, para o treinador saber quando vale pagar de novo.
 */
function scoutView(world: World, player: Player, myClubId: string): ScoutView | null {
  const report = scoutReportFor(world, myClubId, player.id);
  if (!report) return null;
  return {
    low: report.potentialLow,
    high: report.potentialHigh,
    confidence: report.confidence,
    verdictKey: report.verdictKey,
    notes: report.notes.map((nota) => ({ ...nota })),
    stale: isReportStale(world, report),
  };
}

function offerRow(world: World, offer: Offer, myClubId: string): OfferRow | null {
  const player = world.players[offer.playerId];
  if (!player) return null;
  const incoming = offer.toClubId === myClubId;
  const otherId = incoming ? offer.fromClubId : (offer.toClubId ?? "");
  const other = world.clubs[otherId];
  return {
    id: offer.id,
    playerId: player.id,
    playerName: player.name,
    playerPos: player.pos,
    playerStrength: player.strength,
    otherClubId: otherId,
    otherClubName: other?.name ?? "",
    otherClubShort: other?.short ?? "",
    otherClubColors: other?.colors ?? ["#2c3f33", "#2c3f33"],
    amount: offer.amount,
    salary: offer.salary,
    years: offer.years,
    status: offer.status,
    // Save de antes da correção do motor pode ter proposta com rodada da
    // temporada passada; o prazo conta, no máximo, da rodada atual.
    expiresRound: Math.min(offer.createdRound, world.round) + HUMAN_OFFER_WINDOW,
    answered: offer.approved,
  };
}

export function buildClubView(
  world: World,
  clubId: string,
  managerName: string,
  pending: QueuedDecision[],
  saveId: string | null,
  lineup: Lineup,
): ClubView {
  const club = getClub(world, clubId);
  const league = clubLeague(world, clubId);
  const rows = league ? (world.standings[league.id] ?? []) : [];
  const starters = new Set(lineup.starters);
  const bench = new Set(lineup.bench);
  const squadList = clubPlayers(world, clubId);

  const vagas = tacticSlots(club.tactic);
  const slotOf = new Map<string, string>();
  lineup.starters.forEach((id, index) => {
    const pos = vagas[index];
    if (pos) slotOf.set(id, pos);
  });

  const squad: PlayerRow[] = squadList
    .map((player) => playerRow(player, starters, bench, slotOf.get(player.id) ?? null))
    .sort(
      (a, b) =>
        sectorOrder(a.sector) - sectorOrder(b.sector) ||
        Number(b.starter) - Number(a.starter) ||
        b.strength - a.strength,
    );

  const table: TableRow[] = toTableRows(world, rows, clubId);

  const mine = rows.find((row) => row.clubId === clubId);
  const fixture = upcomingFixture(world, clubId);
  const opponent = fixture
    ? world.clubs[fixture.homeId === clubId ? fixture.awayId : fixture.homeId]
    : null;

  const roundsPlayed = Math.max(1, world.round);
  const income =
    club.season.gate + club.season.members + club.season.tv + club.season.sponsor + club.season.prizes;
  const expense = club.season.wages + club.season.maintenance + club.season.interest;
  const moraleAverage =
    squadList.length > 0
      ? squadList.reduce((sum, player) => sum + player.morale, 0) / squadList.length
      : 0;
  const manager = club.managerId ? getManager(world, club.managerId) : null;

  const open = world.offers.filter((offer) => offer.status === "aberta");
  const offersIn = open
    .filter((offer) => offer.toClubId === clubId)
    .map((offer) => offerRow(world, offer, clubId))
    .filter((row): row is OfferRow => row !== null);
  const offersOut = open
    .filter((offer) => offer.fromClubId === clubId)
    .map((offer) => offerRow(world, offer, clubId))
    .filter((row): row is OfferRow => row !== null);

  return {
    season: world.season,
    round: world.round,
    totalRounds: world.totalRounds,
    phase: world.phase,
    managerName,
    managerReputation: manager?.reputation ?? 40,
    saveId,
    club: {
      id: club.id,
      name: club.name,
      short: club.short,
      colors: club.colors,
      crest: club.crest ?? null,
      kit: club.kit ?? null,
      countryName: world.countries[club.countryId]?.name ?? "",
      founded: club.founded,
      founder: club.founder ?? null,
      cash: club.cash,
      debt: club.debt,
      reputation: club.reputation,
      reputationKey: reputationKey(club.reputation),
      ticketPrice: club.ticketPrice,
      stadiumCapacity: club.stadiumCapacity,
      form: club.form,
      leagueName: league?.name ?? "",
      position: league ? positionOf(rows, clubId) : 0,
      hasTable: rows.some((row) => row.played > 0),
      boardLabelKey: club.board.sportingLabelKey,
      boardPatience: Math.round(club.board.patience),
      moraleAverage: Math.round(moraleAverage),
    },
    tactic: {
      formation: club.tactic.formation,
      ...(club.tactic.custom ? { custom: club.tactic.custom.map((slot) => ({ ...slot })) } : {}),
      style: club.tactic.style,
      marking: club.tactic.marking,
      tempo: club.tactic.tempo,
      takers: { ...club.tactic.takers },
      captainId: club.tactic.captainId,
    },
    lineup: {
      starters: [...lineup.starters],
      bench: [...lineup.bench],
      autoSubs: lineup.autoSubs.map((sub) => ({ ...sub })),
    },
    formations: Object.values(FORMATIONS).map((entry) => ({
      name: entry.name,
      slots: [...entry.slots],
    })),
    fitByPosition: buildFitByPosition(squadList),
    finance: {
      cash: club.cash,
      incomePerRound: world.round > 0 ? Math.round(income / roundsPlayed) : null,
      expensePerRound: world.round > 0 ? Math.round(expense / roundsPlayed) : null,
      wagesPerRound: squadWageBill(world, clubId),
      gate: club.season.gate,
      sponsor: club.season.sponsor,
      tv: club.season.tv,
      prizes: club.season.prizes,
    },
    record: {
      played: mine?.played ?? 0,
      wins: mine?.wins ?? 0,
      draws: mine?.draws ?? 0,
      losses: mine?.losses ?? 0,
      goalsFor: mine?.goalsFor ?? 0,
      goalsAgainst: mine?.goalsAgainst ?? 0,
      points: mine?.points ?? 0,
    },
    squad,
    youth: youthRows(world, clubId),
    timeB: buildTimeB(world, clubId),
    table,
    rivals: rivalRows(world, club),
    offersIn,
    offersOut,
    marketOpen: isWindowOpen(world),
    marketOpensRound: isWindowOpen(world)
      ? null
      : world.round < world.settings.midSeasonWindow[0]
        ? world.settings.midSeasonWindow[0]
        : null,
    pending,
    opponent: opponent ? buildOpponent(world, opponent) : null,
    nextMatch:
      fixture && opponent
        ? {
            opponent: opponent.name,
            opponentShort: opponent.short,
            opponentColors: opponent.colors,
            home: fixture.homeId === clubId,
            competition: world.competitions[fixture.competitionId]?.name ?? "",
            stage: fixture.stage,
            round: fixture.round,
            derby: rivalryIntensity(world, clubId, opponent.id) > 0,
          }
        : null,
    calendar: buildCalendar(world, clubId),
    news: world.news
      .filter((item) => item.clubId === clubId || item.clubId === null)
      .slice(-40)
      .reverse()
      .map((item) => ({ id: item.id, kind: item.kind, round: item.round, read: item.read, ...newsPayload(item) })),
    jornal: buildJornal(world, clubId),
    coletiva: null,
    seasonOver: world.phase === "postemporada",
    seasonReport: seasonReportView(world, clubId, pending),
    management: buildManagement(world, clubId),
    cup: buildCup(world, clubId),
    continental: buildContinental(world, clubId),
    state: buildState(world, clubId),
    career: buildCareer(world, managerName),
    debug: {
      seed: world.seed,
      engineVersion: world.engineVersion,
      schemaVersion: world.schemaVersion,
    },
    // Preenchido pela sessão, que é quem conhece os assentos do hotseat.
    seats: [],
    scorers: buildScorers(world, clubId),
  };
}

/**
 * A base do clube, pronta para a tela.
 *
 * O motor já entrega a projeção sem o potencial cru, então aqui só entram o
 * formato da interface e a decisão pendente, que a sessão preenche depois por
 * ser ela quem conhece a fila.
 */
function youthRows(world: World, clubId: string): YouthRow[] {
  return youthViews(world, clubId).map((jovem) => ({
    id: jovem.id,
    name: jovem.name,
    pos: jovem.pos,
    age: jovem.age,
    nationality: jovem.nationality,
    // Sigla do motor, como na ficha do jogador. A palavra escrita ("Destro")
    // nascia aqui, dentro do worker, e por isso saía em português em qualquer
    // idioma; quem a escreve agora é o dicionário, com `rotuloDePe`.
    foot: jovem.foot,
    personality: jovem.personality,
    strength: jovem.strength,
    entryStrength: jovem.entryStrength,
    growth: jovem.growth,
    potentialLow: jovem.potentialLow,
    potentialHigh: jovem.potentialHigh,
    confidence: jovem.confidence,
    verdict: jovem.verdict,
    highlights: [...jovem.highlights],
    seasonsInAcademy: jovem.seasonsInAcademy,
    stagnant: jovem.stagnant,
    ready: jovem.ready,
    lastSeasonAllowed: jovem.lastSeasonAllowed,
    pendingAction: null,
    bSeason: {
      apps: jovem.bSeason.apps,
      goals: jovem.bSeason.goals,
      assists: jovem.bSeason.assists,
      motm: jovem.bSeason.motm,
      cleanSheets: jovem.bSeason.cleanSheets,
      rating: jovem.bSeason.apps > 0 ? Math.round((jovem.bSeason.ratingSum / jovem.bSeason.apps) * 10) / 10 : 0,
      yellow: jovem.bSeason.yellow,
      red: jovem.bSeason.red,
      roundTeams: jovem.bSeason.roundTeams,
    },
    feats: jovem.feats.map((feito) => ({ key: feito.key, season: feito.season, params: { ...feito.params } })),
    injuryRounds: jovem.injuryRounds,
    value: jovem.value,
    lastYear: jovem.age >= YOUTH_MAX_AGE,
  }));
}

/** Um clube do mundo no formato curto que o Time B usa. */
function clubeDoTimeB(world: World, clubId: string): ClubeDoTimeB {
  const club = world.clubs[clubId];
  return {
    id: clubId,
    name: club?.name ?? "?",
    short: club?.short ?? "",
    colors: club?.colors ?? ["#2c3f33", "#2c3f33"],
  };
}

/**
 * O Time B do clube, pronto para a tela.
 *
 * O motor entrega ids; aqui eles viram nome, sigla e cores, e cada lance ganha
 * a marca de ser ou não do seu time, que é o que a tela pinta.
 */
function buildTimeB(world: World, clubId: string): TimeBView {
  const bruto = timeBView(world, clubId);
  const ultima = bruto.lastMatch;
  const nomeDaLiga = (id: string): string => world.competitions[id]?.name ?? "";
  const linhas = bruto.league ? toTableRows(world, bruto.league.rows, clubId) : [];
  const minha = linhas.find((linha) => linha.isMine);
  return {
    coach: bruto.coach
      ? {
          name: bruto.coach.name,
          reputation: bruto.coach.reputation,
          morale: bruto.coach.morale,
          salary: bruto.coach.salary,
          starChance: bruto.coach.starChance,
          fee: bruto.coach.fee,
        }
      : null,
    candidates: bruto.candidates.map((candidato) => ({ ...candidato })),
    intakeLevel: bruto.intakeLevel,
    capacity: bruto.capacity,
    size: bruto.size,
    league: bruto.league ? { competitionName: bruto.league.competitionName, rows: linhas } : null,
    position: minha && minha.played > 0 ? minha.position : null,
    lastMatch: ultima
      ? {
          season: ultima.season,
          round: ultima.round,
          competitionName: nomeDaLiga(ultima.competitionId),
          home: clubeDoTimeB(world, ultima.homeId),
          away: clubeDoTimeB(world, ultima.awayId),
          homeGoals: ultima.homeGoals,
          awayGoals: ultima.awayGoals,
          events: ultima.events.map((lance) => ({
            minute: lance.minute,
            kind: lance.kind,
            mine: lance.clubId === clubId,
            clubShort: world.clubs[lance.clubId]?.short ?? "",
            name: lance.name,
            assist: lance.assist,
            rounds: lance.rounds ?? null,
          })),
          possession: [...ultima.possession],
          shots: [...ultima.shots],
          onTarget: [...ultima.onTarget],
          best: ultima.best.map((melhor) => ({
            name: melhor.name,
            clubShort: world.clubs[melhor.clubId]?.short ?? "",
            mine: melhor.clubId === clubId,
            rating: melhor.rating,
          })),
        }
      : null,
    history: bruto.history.map((temporada) => ({
      season: temporada.season,
      competitionName: temporada.competitionName,
      position: temporada.position,
      played: temporada.played,
      points: temporada.points,
      champion: temporada.champion,
      topScorer: temporada.topScorer ? { ...temporada.topScorer } : null,
      promoted: temporada.promoted.map((entrada) => ({ ...entrada })),
      intake: temporada.intake,
    })),
    current: {
      season: bruto.current.season,
      promoted: bruto.current.promoted.map((entrada) => ({ ...entrada })),
      intake: bruto.current.intake,
      intakeLevel: bruto.current.intakeLevel,
    },
  };
}

/** Os Livres, com a origem de cada um já com nome e escudo. */
export function buildLivres(world: World, clubId: string): LivreRow[] {
  return livresView(world, clubId).map((livre) => ({
    id: livre.id,
    name: livre.name,
    pos: livre.pos,
    age: livre.age,
    nationality: livre.nationality,
    strength: livre.strength,
    fromClub: livre.fromClubId ? clubeDoTimeB(world, livre.fromClubId) : null,
    fromSeason: livre.fromSeason,
    fromRound: livre.fromRound,
    rounds: livre.rounds,
    leavesIn: livre.leavesIn,
    asking: livre.asking,
    highlights: (() => {
      const jogador = world.players[livre.id];
      return jogador ? highlights(jogador) : [];
    })(),
  }));
}

/**
 * Quanto cada jogador do elenco rende em cada posição.
 *
 * Calculado aqui, de uma vez, em vez de uma ida ao worker por vaga clicada: são
 * dez posições por trinta jogadores, barato de montar e instantâneo na tela.
 */
function buildFitByPosition(
  squad: Player[],
): Record<string, Array<{ id: string; rating: number; available: boolean }>> {
  const map: Record<string, Array<{ id: string; rating: number; available: boolean }>> = {};
  for (const pos of POSITIONS) {
    map[pos] = squad
      .map((player) => ({
        id: player.id,
        rating: Math.round(effectiveStrength(player, pos as Position)),
        available: player.injuryRounds === 0 && player.suspensionRounds === 0,
      }))
      .sort((a, b) => Number(b.available) - Number(a.available) || b.rating - a.rating);
  }
  return map;
}

/**
 * O adversário do próximo jogo, visto de fora.
 *
 * A escalação é a que ele deixou guardada, e a automática quando não há
 * nenhuma: é a mesma que o motor usaria se a rodada rodasse agora. A nota de
 * cada um é a da vaga, com improviso, moral e condição, que é o número que a
 * sua escalação também mostra. Comparar os dois só vale se forem o mesmo número.
 */
function buildOpponent(world: World, club: Club): OpponentView {
  const league = clubLeague(world, club.id);
  const rows = league ? (world.standings[league.id] ?? []) : [];
  const row = rows.find((entry) => entry.clubId === club.id);
  const slots: string[] = tacticSlots(club.tactic);
  const lineup = club.lineup ?? autoLineup(world, club);
  const squad = clubPlayers(world, club.id);
  const byId = new Map(squad.map((player) => [player.id, player]));

  const starters = slots.map((pos, index) => {
    const player = byId.get(lineup.starters[index] ?? "");
    return {
      name: player?.name ?? "",
      pos,
      rating: player ? Math.round(effectiveStrength(player, pos as Position)) : 0,
    };
  });

  const highlights: OpponentView["highlights"] = [];
  const used = new Set<string>();
  const push = (player: Player | undefined, kind: OpponentView["highlights"][number]["kind"], value: number): void => {
    if (!player || used.has(player.id)) return;
    used.add(player.id);
    highlights.push({ name: player.name, pos: player.pos, rating: player.strength, kind, value });
  };
  const scorer = [...squad].sort((a, b) => b.season.goals - a.season.goals)[0];
  if (scorer && scorer.season.goals > 0) push(scorer, "gols", scorer.season.goals);
  const assister = [...squad].sort((a, b) => b.season.assists - a.season.assists)[0];
  if (assister && assister.season.assists > 0) push(assister, "assistencias", assister.season.assists);
  if (club.tactic.captainId) push(byId.get(club.tactic.captainId), "capitao", 0);

  const inEleven = new Set(lineup.starters);
  const absences: OpponentView["absences"] = [];
  for (const player of squad) {
    if (player.injuryRounds > 0) {
      absences.push({ name: player.name, pos: player.pos, kind: "lesao", count: player.injuryRounds });
    } else if (player.suspensionRounds > 0) {
      absences.push({ name: player.name, pos: player.pos, kind: "suspenso", count: player.suspensionRounds });
    } else if (inEleven.has(player.id) && player.season.yellow % 3 === 2) {
      absences.push({ name: player.name, pos: player.pos, kind: "pendurado", count: player.season.yellow });
    }
  }

  return {
    name: club.name,
    short: club.short,
    colors: club.colors,
    position: league ? positionOf(rows, club.id) : 0,
    points: row?.points ?? 0,
    hasTable: rows.some((entry) => entry.played > 0),
    form: club.form,
    formation: club.tactic.formation,
    slots,
    style: club.tactic.style,
    marking: club.tactic.marking,
    tempo: club.tactic.tempo,
    starters,
    highlights,
    absences: absences.slice(0, 5),
  };
}

function rivalRows(world: World, club: Club): RivalRow[] {
  const rows: RivalRow[] = [];
  for (const rival of club.rivals) {
    const other = world.clubs[rival.clubId];
    if (!other) continue;
    rows.push({
      clubId: other.id,
      name: other.name,
      short: other.short,
      colors: other.colors,
      intensity: rival.intensity,
      labelKey: rivalryKey(rival.intensity),
    });
  }
  return rows;
}

export function summarizeMatch(
  world: World,
  clubId: string,
  results: MatchResult[],
): MatchSummary | null {
  const result = results.find((r) => r.homeId === clubId || r.awayId === clubId);
  if (!result) return null;

  const mineIsHome = result.homeId === clubId;
  const myGoals = mineIsHome ? result.homeGoals : result.awayGoals;
  const theirGoals = mineIsHome ? result.awayGoals : result.homeGoals;
  const fixture = world.fixtures[result.fixtureId];
  const home = world.clubs[result.homeId];
  const away = world.clubs[result.awayId];

  return {
    homeName: home?.name ?? "?",
    homeShort: home?.short ?? "",
    homeColors: home?.colors ?? ["#2c3f33", "#2c3f33"],
    awayName: away?.name ?? "?",
    awayShort: away?.short ?? "",
    awayColors: away?.colors ?? ["#2c3f33", "#2c3f33"],
    homeGoals: result.homeGoals,
    awayGoals: result.awayGoals,
    competitionName: fixture ? (world.competitions[fixture.competitionId]?.name ?? "") : "",
    stage: fixture?.stage ?? "",
    derby: rivalryIntensity(world, result.homeId, result.awayId) > 0,
    myResult: myGoals > theirGoals ? "V" : myGoals === theirGoals ? "E" : "D",
    // A posse por trecho é desenho do campo, não narração; a chance perdida
    // já ficava de fora do resumo.
    events: result.events
      .filter((event) => !["chance", "posse", "falta", "impedimento"].includes(event.kind))
      .map((event) => ({
        minute: event.minute,
        kind: event.kind,
        key: event.key,
        params: event.params,
        mine: event.clubId === clubId,
        clubShort: world.clubs[event.clubId]?.short ?? "",
      })),
    factors: result.factors.map((factor) => ({ key: factor.key, params: factor.params })),
    possession: mineIsHome ? result.home.possession : result.away.possession,
    chances: mineIsHome ? result.home.chances : result.away.chances,
    opponentChances: mineIsHome ? result.away.chances : result.home.chances,
  };
}

export { expectedSalary, type Position };

/**
 * Balanço da temporada para a tela de fim de ano.
 *
 * O motor entrega o relatório pronto por clube humano. A tela só precisa saber,
 * além disso, quais renovações já estão na fila, para não oferecer duas vezes o
 * mesmo botão.
 */
/**
 * O clube que chamou, por dentro.
 *
 * No fim da temporada a escolha era às cegas: nome, liga, objetivo e caixa.
 * Aqui vem o que se olha antes de assinar: o elenco inteiro, a força dele, a
 * folha, a dívida e o estádio.
 */
function previaDoClube(world: World, clubId: string): ClubPreview | null {
  const club = world.clubs[clubId];
  if (!club) return null;
  const league = clubLeague(world, clubId);
  const rows = league ? (world.standings[league.id] ?? []) : [];
  const jogadores = clubPlayers(world, clubId);
  const squad = jogadores
    .map((player) => ({
      id: player.id,
      name: player.name,
      pos: player.pos,
      sector: sectorOf(player.pos),
      age: player.age,
      strength: player.strength,
    }))
    .sort((a, b) => sectorOrder(a.sector) - sectorOrder(b.sector) || b.strength - a.strength);
  return {
    reputation: club.reputation,
    reputationKey: reputationKey(club.reputation),
    stadiumCapacity: club.stadiumCapacity,
    debt: club.debt,
    wagesPerRound: squadWageBill(world, clubId),
    position: rows.some((row) => row.played > 0) ? positionOf(rows, clubId) : 0,
    squadStrength: Math.round(squadStrength(world, clubId)),
    averageAge:
      jogadores.length > 0
        ? Math.round((jogadores.reduce((soma, p) => soma + p.age, 0) / jogadores.length) * 10) / 10
        : 0,
    squad,
  };
}

/**
 * O preço que o clube dono aceita sem pensar.
 *
 * É a mesma conta do `evaluateOffer` do motor, lida pelo lado de quem compra:
 * o vendedor aceita de cara quando a oferta passa de 1,12 vez o valor, com
 * desconto para quem está com o caixa negativo ou pôs o jogador à venda, e
 * nunca abaixo do piso. Cláusula de rescisão menor que isso vale como preço.
 */
export function precoDeCompra(world: World, player: Player): number | null {
  if (!player.clubId) return 0;
  if (player.loan) return null;
  if (humanClubIds(world).includes(player.clubId)) return null;
  const vendedor = world.clubs[player.clubId];
  if (!vendedor) return null;
  const valor = playerValue(player, world.season);
  const piso = player.transferListed ? valor * 0.55 : minimumAcceptable(player, world.season);
  const desconto = (vendedor.cash < 0 ? 0.25 : 0) + (player.transferListed ? 0.25 : 0);
  let preco = Math.max(piso, valor * (1.12 - desconto));
  const clausula = player.contract.releaseClause;
  if (clausula && clausula >= piso) preco = Math.min(preco, clausula);
  return Math.ceil(preco / 10_000) * 10_000;
}

function seasonReportView(
  world: World,
  clubId: string,
  pending: QueuedDecision[],
): SeasonReportView | null {
  const report = world.seasonReports[clubId];
  if (!report) return null;

  // Por identificador, e não pelo nome dentro da frase: a busca antiga era por
  // pedaço de texto e marcava como renovado quem só tinha uma bronca na fila.
  const renovando = new Set(
    pending
      .filter((entry) => entry.key === "sessao.fila.renovar" && entry.playerId !== null)
      .map((entry) => entry.playerId as string),
  );

  // O motor guarda a conquista pelo identificador, que é o que fica na carreira
  // para sempre. A chave do nome e da descrição sai do catálogo dele.
  const chavesDeConquista = new Map(
    achievementCatalogue().map((entrada) => [entrada.id, entrada.key as string]),
  );

  return {
    season: report.season,
    clubName: report.clubName,
    leagueName: report.leagueName,
    position: report.position,
    played: report.played,
    wins: report.wins,
    draws: report.draws,
    losses: report.losses,
    points: report.points,
    goalsFor: report.goalsFor,
    goalsAgainst: report.goalsAgainst,
    target: report.target,
    targetLabelKey: report.targetLabelKey,
    verdict: report.verdict,
    fired: report.fired,
    titles: [...report.titles],
    finance: { ...report.finance },
    topScorer: report.topScorer ? { ...report.topScorer } : null,
    bestRated: report.bestRated ? { ...report.bestRated } : null,
    youth: report.youth.map((entry) => ({ ...entry })),
    expiring: report.expiring.map((entry) => ({
      playerId: entry.playerId,
      name: entry.name,
      pos: entry.pos,
      age: entry.age,
      strength: entry.strength,
      salary: entry.salary,
      renewing: renovando.has(entry.playerId),
    })),
    offers: report.offers.map((offer) => ({
      clubId: offer.clubId,
      clubName: offer.clubName,
      clubShort: offer.clubShort,
      colors: offer.colors,
      countryName: offer.countryName,
      leagueName: offer.leagueName,
      objectiveKey: offer.objectiveKey,
      budget: offer.budget,
      preview: previaDoClube(world, offer.clubId),
    })),
    // A lista pode não existir num balanço gravado antes de as conquistas
    // entrarem, que é a mesma guarda que a migração do motor usa. E conquista
    // que o catálogo não conhece fica de fora: é de uma versão mais nova do
    // motor, e sem a chave não há nome para mostrar.
    achievements: (report.newAchievements ?? []).flatMap((conquista) => {
      const key = chavesDeConquista.get(conquista.id);
      return key ? [{ id: conquista.id, key, season: conquista.season }] : [];
    }),
  };
}

/**
 * Disputa aberta por um jogador.
 *
 * O motor resolve as propostas da rodada da maior para a menor, que é o leilão
 * herdado do Elifoot. A tela só precisa dizer que a disputa existe e qual é o
 * maior lance, para o treinador saber se vale cobrir.
 */
function auctionView(
  world: World,
  player: Player,
  myClubId: string,
): PlayerDetail["auction"] {
  const abertas = world.offers.filter(
    (offer) => offer.playerId === player.id && offer.status === "aberta",
  );
  if (abertas.length < 2) return null;

  const maior = abertas.reduce((melhor, offer) => (offer.amount > melhor.amount ? offer : melhor));
  return {
    bidders: new Set(abertas.map((offer) => offer.fromClubId)).size,
    topBid: maior.amount,
    mineIsTop: maior.fromClubId === myClubId,
  };
}
